import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  TextField,
  Alert,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { adminAccessService } from '../../services'
import RolePermissionsEditor from './RolePermissionsEditor'
import { summarizePermissionsForHumans } from '../../utils/adminPermissions'

export default function ManageRolesDialog({
  open,
  onClose,
  canWriteRoles,
  permissionSchema,
  rolePresets,
  onRolesChanged,
  onSnackbar,
}) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorMode, setEditorMode] = useState('view') // view | edit
  const [editingRole, setEditingRole] = useState(null)
  const [editName, setEditName] = useState('')
  const [editPermissions, setEditPermissions] = useState({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await adminAccessService.listRoles()
      setRows(Array.isArray(data) ? data : [])
    } catch (err) {
      onSnackbar?.({
        severity: 'error',
        message: err.response?.data?.message || 'Could not load roles',
      })
    } finally {
      setLoading(false)
    }
  }, [onSnackbar])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  const openView = (role) => {
    setEditingRole(role)
    setEditName(role.display_name)
    setEditPermissions(structuredClone(role.permissions || {}))
    setEditorMode('view')
    setEditorOpen(true)
  }

  const openEdit = (role) => {
    if (role.is_system) return
    setEditingRole(role)
    setEditName(role.display_name)
    setEditPermissions(structuredClone(role.permissions || {}))
    setEditorMode('edit')
    setEditorOpen(true)
  }

  const saveEdit = async () => {
    if (!editingRole || editorMode !== 'edit') return
    try {
      setSaving(true)
      await adminAccessService.updateRole(editingRole.id, {
        display_name: editName.trim(),
        permissions: editPermissions,
      })
      onSnackbar?.({ severity: 'success', message: 'Role updated' })
      setEditorOpen(false)
      await load()
      onRolesChanged?.()
    } catch (err) {
      onSnackbar?.({
        severity: 'error',
        message: err.response?.data?.message || 'Could not update role',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (role) => {
    if (role.is_system) return
    if (role.assignment_count > 0) {
      onSnackbar?.({
        severity: 'warning',
        message: `Remove this role from ${role.assignment_count} ${
          role.assignment_count === 1 ? 'person' : 'people'
        } before deleting it.`,
      })
      return
    }
    if (!window.confirm(`Delete the role “${role.display_name}”?`)) return
    try {
      await adminAccessService.deleteRole(role.id)
      onSnackbar?.({ severity: 'success', message: 'Role deleted' })
      await load()
      onRolesChanged?.()
    } catch (err) {
      onSnackbar?.({
        severity: 'error',
        message: err.response?.data?.message || 'Could not delete role',
      })
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Manage roles</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Built-in roles cannot be changed. Custom roles can be edited or deleted when nobody is using them.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>What it allows</TableCell>
                  <TableCell align="center">People</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={22} sx={{ my: 1 }} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No roles found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((role) => {
                    const summary = summarizePermissionsForHumans(
                      permissionSchema,
                      role.permissions,
                    )
                    return (
                      <TableRow key={role.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role.display_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={role.is_system ? 'Built-in' : 'Custom'}
                            sx={{ mt: 0.5, height: 22 }}
                            color={role.is_system ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {summary.slice(0, 3).join(' · ')}
                            {summary.length > 3 ? '…' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{role.assignment_count}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label="View role"
                            onClick={() => openView(role)}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                          {canWriteRoles && !role.is_system && (
                            <>
                              <IconButton
                                size="small"
                                aria-label="Edit role"
                                onClick={() => openEdit(role)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Delete role"
                                onClick={() => handleDelete(role)}
                                disabled={role.assignment_count > 0}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editorOpen}
        onClose={() => !saving && setEditorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editorMode === 'edit' ? 'Edit role' : 'View role'}
          {editingRole ? ` — ${editingRole.display_name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {editingRole?.is_system && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Built-in roles are view-only.
            </Alert>
          )}
          {editorMode === 'edit' && (
            <TextField
              fullWidth
              label="Role name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          <RolePermissionsEditor
            permissionSchema={permissionSchema}
            rolePresets={editorMode === 'edit' ? rolePresets : []}
            selectedPermissions={editPermissions}
            onChange={setEditPermissions}
            readOnly={editorMode !== 'edit'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>
            {editorMode === 'edit' ? 'Cancel' : 'Close'}
          </Button>
          {editorMode === 'edit' && (
            <Button
              variant="contained"
              onClick={saveEdit}
              disabled={saving || !editName.trim()}
            >
              {saving ? <CircularProgress size={22} /> : 'Save changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
